import { CONNECTION_IDLE, CONNECTION_CONNECTED } from './aiConnection';
import actions from './aiActions';

// ============================================================================
// FE-03 前端 AI 会话状态管理（共享基石）
// 集中管理：会话、任务、消息、子任务、事件、连接状态。
// 关键 id 持久化到 localStorage，刷新后可恢复正在执行的任务。
// 后端契约见 AiTaskController / AiConversationController（/api/ai，经 baseRequest 带 token）。
// 分页参数：currentPage + pageSize（以实际后端为准）。
// actions 见 ./aiActions.js；SSE 连接实现见 ./aiConnection.js。
// ============================================================================

const STORAGE_PREFIX = 'lingxi:ai:';

function writeStorage(key, value) {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
  } catch (e) {
    // localStorage 不可用（隐私模式/配额）时静默降级，不影响内存态
  }
}

function removeStorage(key) {
  try {
    localStorage.removeItem(STORAGE_PREFIX + key);
  } catch (e) {
    // 同上
  }
}

function initialState() {
  return {
    conversations: [],
    conversationsTotal: 0,
    conversationsPage: 1,
    conversationsPageSize: 20,
    messages: {},
    messagesTotal: {},
    tasks: {},
    subtasks: {},
    events: {},
    connection: {},
    currentConversationId: null,
    currentTaskId: null,
    loading: {
      conversations: false,
      messages: false,
      task: false,
      createTask: false,
      retrySubtask: false,
    },
    error: null,
  };
}

function upsertTask(state, snapshot) {
  if (!snapshot || !snapshot.taskId) return;
  state.tasks = { ...state.tasks, [snapshot.taskId]: snapshot };
  if (Array.isArray(snapshot.subtasks)) {
    state.subtasks = { ...state.subtasks, [snapshot.taskId]: snapshot.subtasks };
  }
}

function appendEvent(state, taskId, event) {
  if (!taskId || !event) return;
  const list = state.events[taskId] || [];
  if (list.some((e) => e.eventId === event.eventId)) return;
  state.events = { ...state.events, [taskId]: [...list, event] };
}

function setConnection(state, taskId, status, error = null) {
  state.connection = {
    ...state.connection,
    [taskId]: { status, error },
  };
}

export default {
  namespaced: true,
  state: initialState(),
  getters: {
    currentMessages: (state) =>
      state.currentConversationId ? state.messages[state.currentConversationId] || [] : [],
    currentTask: (state) => (state.currentTaskId ? state.tasks[state.currentTaskId] || null : null),
    currentSubtasks: (state) =>
      state.currentTaskId ? state.subtasks[state.currentTaskId] || [] : [],
    currentEvents: (state) =>
      state.currentTaskId ? state.events[state.currentTaskId] || [] : [],
    currentConnection: (state) =>
      state.currentTaskId
        ? state.connection[state.currentTaskId] || { status: CONNECTION_IDLE, error: null }
        : { status: CONNECTION_IDLE, error: null },
    messagesByConversation: (state) => (conversationId) =>
      state.messages[conversationId] || [],
    taskById: (state) => (taskId) => state.tasks[taskId] || null,
    subtasksByTask: (state) => (taskId) => state.subtasks[taskId] || [],
    eventsByTask: (state) => (taskId) => state.events[taskId] || [],
    connectionByTask: (state) => (taskId) =>
      state.connection[taskId] || { status: CONNECTION_IDLE, error: null },
    isConnected: (state) => {
      const conn = state.currentTaskId
        ? state.connection[state.currentTaskId]
        : null;
      return Boolean(conn && conn.status === CONNECTION_CONNECTED);
    },
    hasRunningTask: (state) =>
      Object.values(state.tasks).some((t) => t && (t.status === 'RUNNING' || t.status === 'CREATED')),
  },
  mutations: {
    SET_CONVERSATIONS(state, { list, total, currentPage, pageSize }) {
      state.conversations = list || [];
      state.conversationsTotal = total || 0;
      state.conversationsPage = currentPage || 1;
      state.conversationsPageSize = pageSize || 20;
    },
    SET_MESSAGES(state, { conversationId, list, total }) {
      state.messages = { ...state.messages, [conversationId]: list || [] };
      state.messagesTotal = { ...state.messagesTotal, [conversationId]: total || 0 };
    },
    APPEND_MESSAGE(state, { conversationId, message }) {
      if (!conversationId || !message) return;
      const list = state.messages[conversationId] || [];
      if (list.some((m) => m.id === message.id)) return;
      state.messages = { ...state.messages, [conversationId]: [...list, message] };
    },
    SET_TASK(state, snapshot) {
      upsertTask(state, snapshot);
    },
    SET_TASK_ERROR(state, { taskId, message }) {
      if (!taskId) return;
      const existing = state.tasks[taskId];
      const snapshot = {
        ...(existing || { taskId }),
        taskId,
        status: 'FAILED',
        errorMessage: message || 'AI 任务执行失败',
      };
      state.tasks = { ...state.tasks, [taskId]: snapshot };
      state.error = message || 'AI 任务执行失败';
    },
    SET_SUBTASKS(state, { taskId, subtasks }) {
      if (!taskId) return;
      state.subtasks = { ...state.subtasks, [taskId]: subtasks || [] };
    },
    APPEND_EVENT(state, { taskId, event }) {
      appendEvent(state, taskId, event);
    },
    SET_CONNECTION(state, { taskId, status, error = null }) {
      setConnection(state, taskId, status, error);
    },
    SET_CURRENT_CONVERSATION(state, conversationId) {
      state.currentConversationId = conversationId;
      writeStorage('currentConversationId', conversationId);
    },
    SET_CURRENT_TASK(state, taskId) {
      state.currentTaskId = taskId;
      writeStorage('currentTaskId', taskId);
    },
    RESTORE(state, { conversationId, taskId }) {
      state.currentConversationId = conversationId || null;
      state.currentTaskId = taskId || null;
    },
    SET_LOADING(state, { key, value }) {
      state.loading = { ...state.loading, [key]: value };
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    },
    RESET(state) {
      const fresh = initialState();
      Object.keys(fresh).forEach((k) => {
        state[k] = fresh[k];
      });
      removeStorage('currentConversationId');
      removeStorage('currentTaskId');
    },
  },
  actions,
};
